import React, { Suspense } from "react";
import { Route, Switch } from "react-router-dom";
import WithHeaderLayout from "./layouts/WithHeaderLayout";
import PlainLayout from "./layouts/PlainLayout";
import V2Layout from "./layouts/v2-layout";
import V2HomeLayout from "./layouts/v2-home-layout";
import { isHeaderLayoutPath, isHomeLayoutPath } from "./layouts/headerLayoutPaths";
import ChunkErrorBoundary from "./components/ChunkErrorBoundary";
import lazyWithRetry from "./utils/lazyWithRetry";

const BlogPage = lazyWithRetry(() => import("./components/blog/BlogPage")); // ponytail: code-split route
const Blogs = lazyWithRetry(() => import("./components/blog/Blogs")); // ponytail: code-split route
const Games = lazyWithRetry(() => import("./containers/Games")); // ponytail: code-split route
const Seats = lazyWithRetry(() => import("./containers/Seats")); // ponytail: code-split route
const Ticket = lazyWithRetry(() => import("./containers/Ticket")); // ponytail: code-split route
const Checkout = lazyWithRetry(() => import("./containers/Checkout")); // ponytail: code-split route
const Home = lazyWithRetry(() => import("./containers/home")); // ponytail: code-split route
const Sell = lazyWithRetry(() => import("./containers/Sell")); // ponytail: code-split route
const ThankYou = lazyWithRetry(() => import("./containers/ThankYou")); // ponytail: code-split route
const WhyUsPage = lazyWithRetry(() => import("./containers/WhyUsPage")); // ponytail: code-split route
const FaqPage = lazyWithRetry(() => import("./containers/FaqPage")); // ponytail: code-split route
const Legal = lazyWithRetry(() => import("./containers/Legal")); // ponytail: code-split route
const HowToAcceptMobileTransfer = lazyWithRetry(() => import("./containers/HowToAcceptMobileTransfer")); // ponytail: code-split route
const HowToMobileTransfer = lazyWithRetry(() => import("./containers/HowToMobileTransfer")); // ponytail: code-split route
const NotFound = lazyWithRetry(() => import("./containers/NotFound")); // ponytail: code-split route
const SignUp = lazyWithRetry(() => import("./containers/SignUp")); // ponytail: code-split route
const Login = lazyWithRetry(() => import("./containers/Login")); // ponytail: code-split route
const SetPrices = lazyWithRetry(() => import("./containers/SetPrices")); // ponytail: code-split route
const Listings = lazyWithRetry(() => import("./containers/Listings")); // ponytail: code-split route
const Orders = lazyWithRetry(() => import("./containers/Orders")); // ponytail: code-split route
const NewListing = lazyWithRetry(() => import("./containers/NewListing")); // ponytail: code-split route
const AccountVerification = lazyWithRetry(() => import("./containers/AccountVerification")); // ponytail: code-split route
const EmailVerification = lazyWithRetry(() => import("./containers/EmailVerification")); // ponytail: code-split route
const AccountSettings = lazyWithRetry(() => import("./containers/AccountSettings")); // ponytail: code-split route
const ChangePassword = lazyWithRetry(() => import("./containers/ChangePassword")); // ponytail: code-split route
const ForgotPassword = lazyWithRetry(() => import("./containers/ForgotPassword")); // ponytail: code-split route
const ResetPassword = lazyWithRetry(() => import("./containers/ResetPassword")); // ponytail: code-split route
const NewPassword = lazyWithRetry(() => import("./containers/NewPassword")); // ponytail: code-split route
const EmergencyMode = lazyWithRetry(() => import("./containers/emergency-mode")); // ponytail: code-split route
const SellerPage = lazyWithRetry(() => import("./containers/SellerPage")); // ponytail: code-split route
const TrackOrder = lazyWithRetry(() => import("./containers/TrackOrder")); // ponytail: code-split route
const Dashboard = lazyWithRetry(() => import("./containers/Dashboard")); // ponytail: code-split route
const GiveawayEntry = lazyWithRetry(() => import("./containers/GiveawayEntry")); // ponytail: code-split route

const WithNewLayoutRoute = ({
  component: Component,
  props: cProps,
  ...rest
}) => {
  return (
    <Route
      {...rest}
      render={(props) => (
        <V2Layout {...props} {...cProps}>
          <Component {...props} {...cProps} />
        </V2Layout>
      )}
    />
  );
};

const PlainLayoutRoute = ({ component: Component, props: cProps, ...rest }) => {
  return (
    <Route
      {...rest}
      render={(props) => (
        <PlainLayout {...props} {...cProps}>
          <Component {...props} {...cProps} />
        </PlainLayout>
      )}
    />
  );
};

const pageFallback = <div style={{ minHeight: "40vh" }} />;

const renderPage = (Component, childProps) => (props) => (
  <Component {...props} {...childProps} />
);

const HeaderLayoutPages = ({ childProps }) => (
  <Suspense fallback={pageFallback}>
  <Switch>
    <Route path="/:teamSlug/games" exact render={renderPage(Games, childProps)} />
    <Route path="/listings/new" exact render={renderPage(NewListing, childProps)} />
    <Route path="/listings" exact render={renderPage(Listings, childProps)} />
    <Route path="/orders" exact render={renderPage(Orders, childProps)} />
    <Route path="/dashboard" exact render={renderPage(Dashboard, childProps)} />
    <Route path="/thankyou/:id" exact render={renderPage(ThankYou, childProps)} />
    <Route path="/why-us" exact render={renderPage(WhyUsPage, childProps)} />
    <Route path="/faqs" exact render={renderPage(FaqPage, childProps)} />
    <Route path="/legal" exact render={renderPage(Legal, childProps)} />
    <Route path="/how-tos/accept-mobile-transfer" exact render={renderPage(HowToAcceptMobileTransfer, childProps)} />
    <Route path="/how-tos/mobile-transfer" exact render={renderPage(HowToMobileTransfer, childProps)} />
    <Route path="/sell/set-prices" exact render={renderPage(SetPrices, childProps)} />
    <Route path="/login" exact render={renderPage(Login, childProps)} />
    <Route path="/account-verification" exact render={renderPage(AccountVerification, childProps)} />
    <Route path="/account" exact render={renderPage(AccountSettings, childProps)} />
    <Route path="/account/password" exact render={renderPage(ChangePassword, childProps)} />
    <Route path="/forgot-password" exact render={renderPage(ForgotPassword, childProps)} />
    <Route path="/reset-password" exact render={renderPage(ResetPassword, childProps)} />
    <Route path="/new-password" exact render={renderPage(NewPassword, childProps)} />
    <Route path="/under-maintenance" exact render={renderPage(EmergencyMode, childProps)} />
    <Route path="/email-verification" exact render={renderPage(EmailVerification, childProps)} />
    <Route path="/track-order" exact render={renderPage(TrackOrder, childProps)} />
  </Switch>
  </Suspense>
);

export default ({ childProps }) => (
  <ChunkErrorBoundary>
  <Suspense fallback={<div style={{height:"100vh",width:"100vw"}} />}>
    <Switch>
    <WithNewLayoutRoute
      path="/sell"
      exact
      component={Sell}
      props={childProps}
    />
    <PlainLayoutRoute
      path="/seats/:slug/:id/"
      exact
      component={Seats}
      props={childProps}
    />
    <PlainLayoutRoute
      path="/games/:id/seats"
      exact
      component={Seats}
      props={childProps}
    />
    <WithNewLayoutRoute
      path="/seats/:slug/:gameId/:seatId"
      exact
      component={Ticket}
      props={childProps}
    />
    <WithNewLayoutRoute
      path="/games/:gameId/seats/:seatId"
      exact
      component={Ticket}
      props={childProps}
    />
    <PlainLayoutRoute
      path="/test-seats/:slug/:id/"
      exact
      component={Seats}
      props={childProps}
    />
    <PlainLayoutRoute
      path="/test-seats/:slug/:id"
      exact
      component={Seats}
      props={childProps}
    />
    <WithNewLayoutRoute
      path="/test-seats/:slug/:gameId/:seatId"
      exact
      component={Ticket}
      props={childProps}
    />
    <PlainLayoutRoute
      path="/checkout"
      exact
      component={Checkout}
      props={childProps}
    />
    <WithNewLayoutRoute
      path="/signup"
      exact
      component={SignUp}
      props={childProps}
    />
    <WithNewLayoutRoute
      path="/blogs/:blogName"
      exact
      component={BlogPage}
      props={childProps}
    />
    <WithNewLayoutRoute
      path="/blogs"
      exact
      component={Blogs}
      props={childProps}
    />
    <WithNewLayoutRoute
      path="/not-found/"
      exact
      component={NotFound}
      props={childProps}
    />
    <PlainLayoutRoute
      path="/giveaway/:campaignId"
      exact
      component={GiveawayEntry}
      props={childProps}
    />
    <Route
      path="/"
      render={(routeProps) => {
        const pathname = routeProps.location.pathname;
        if (isHomeLayoutPath(pathname)) {
          return (
            <V2HomeLayout {...routeProps} {...childProps}>
              <Suspense fallback={pageFallback}>
              <Switch>
                <Route path="/" exact render={renderPage(Home, childProps)} />
                <Route path="/:sellerSlug" exact render={renderPage(SellerPage, childProps)} />
              </Switch>
              </Suspense>
            </V2HomeLayout>
          );
        }
        if (isHeaderLayoutPath(pathname)) {
          return (
            <WithHeaderLayout {...routeProps} {...childProps}>
              <HeaderLayoutPages childProps={childProps} />
            </WithHeaderLayout>
          );
        }
        return (
          <V2Layout {...routeProps} {...childProps}>
            <NotFound {...routeProps} {...childProps} />
          </V2Layout>
        );
      }}
    />
  </Switch>
  </Suspense>
  </ChunkErrorBoundary>
);
